import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Scissors, Mail, Check, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [debugUrl, setDebugUrl] = useState(""); // shown only when backend reports email-not-configured

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSent(true);
      if (data?.debug_reset_url) setDebugUrl(data.debug_reset_url);
      toast.success("Check your email");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Couldn't send reset email");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="forgot-password-page">
      <header className="border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-4 flex items-center gap-3">
          <div className="w-9 h-9 bg-zinc-900 text-white flex items-center justify-center">
            <Scissors className="w-4 h-4" strokeWidth={1.75} />
          </div>
          <div className="text-base font-semibold tracking-tight text-zinc-900 leading-none">BreakItDown</div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-3">Forgot password</p>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-none text-zinc-900 mb-3">
            We'll email you a link.
          </h1>
          <p className="text-sm text-zinc-600 mb-8">Enter the email on your account. We'll send a one-time reset link that expires in 1 hour.</p>

          {sent ? (
            <div className="space-y-4">
              <div className="border border-emerald-300 bg-emerald-50 px-4 py-4 flex items-start gap-3" data-testid="forgot-success">
                <Check className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" strokeWidth={2} />
                <div>
                  <p className="text-sm font-semibold text-emerald-900">If that email is registered, a reset link is on its way.</p>
                  <p className="text-xs text-emerald-800 mt-1">Didn't get it? Check spam, then try again in a minute.</p>
                </div>
              </div>
              {debugUrl && (
                <div className="border border-amber-300 bg-amber-50 px-4 py-3" data-testid="forgot-debug-url">
                  <p className="text-xs font-semibold text-amber-900 mb-1">Dev mode (email integration not configured)</p>
                  <p className="text-[11px] text-amber-800 mb-2">Use this link manually:</p>
                  <a href={debugUrl} className="text-xs text-amber-900 break-all underline">{debugUrl}</a>
                </div>
              )}
              <p className="text-sm text-zinc-500 text-center pt-2">
                <Link to="/login" className="text-zinc-900 font-medium underline-offset-4 hover:underline">Back to sign in</Link>
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div className="border border-zinc-300 focus-within:border-[#002FA7] flex items-center">
                <span className="px-3 text-zinc-400"><Mail className="w-4 h-4" strokeWidth={1.5} /></span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="flex-1 h-12 outline-none text-sm pr-3 bg-transparent"
                  data-testid="forgot-email-input"
                  autoFocus
                />
              </div>

              {error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 flex items-start gap-2" data-testid="forgot-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.75} /> {error}
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                className="w-full h-12 bg-zinc-900 hover:bg-[#002FA7] disabled:opacity-50 text-white text-sm font-medium uppercase tracking-[0.12em] transition-colors"
                data-testid="forgot-submit"
              >
                {busy ? "Sending..." : "Send reset link"}
              </button>

              <p className="text-sm text-zinc-500 text-center pt-3">
                Remembered? <Link to="/login" className="text-zinc-900 font-medium underline-offset-4 hover:underline">Sign in</Link>
              </p>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
